import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { runCommand } from '@/services/commandRouter';
import { LocalCommandResult } from '@/types/orchestrator';

const SUGGESTION_KEYS = [
  'attention', 'atRisk', 'pendingApprovals', 'overdueFollowups', 'expiringContracts',
  'summarizeActivity', 'connectorProblems', 'prepareFollowups', 'runDiagnostics',
] as const;

interface HistoryEntry {
  query: string;
  result: LocalCommandResult;
}

/**
 * AI COMMAND — the primary human-to-Ugarits intelligence interface
 * (Phase D §3). Every result on this page is either a real local answer
 * from the Local Command Router (§4) — never AI prose — or an honest
 * "AI Provider Not Connected" state. Recent commands are session-only
 * (component state), not persisted; a future phase can move this into
 * the demo repository the same way Overview/Settings already do.
 */
export function AICommandPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [running, setRunning] = useState(false);

  async function submit(q: string) {
    const trimmed = q.trim();
    if (!trimmed) return;
    setRunning(true);
    const result = await runCommand(trimmed);
    setHistory((h) => [{ query: trimmed, result }, ...h].slice(0, 20));
    setQuery('');
    setRunning(false);
  }

  const latest = history[0];

  return (
    <div>
      <h1>{t('nav.aiCommand')}</h1>
      <p style={{ color: 'var(--text-2)', marginTop: -8, marginBottom: 16 }}>{t('aiCommand.subtitle')}</p>

      <div className="card ai-command-input-card">
        <input
          className="text-input ai-command-input"
          placeholder={t('aiCommand.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit(query)}
        />
        <button className="btn-primary" disabled={running || !query.trim()} onClick={() => submit(query)}>{t('aiCommand.run')}</button>
      </div>

      <div className="overview-section-label" style={{ marginTop: 18 }}>{t('aiCommand.suggested')}</div>
      <div className="ai-command-suggestions">
        {SUGGESTION_KEYS.map((k) => (
          <button key={k} className="ai-command-chip" onClick={() => submit(t(`aiCommand.suggestions.${k}`))}>
            {t(`aiCommand.suggestions.${k}`)}
          </button>
        ))}
      </div>

      {latest && (
        <div className="card ai-command-result" style={{ marginTop: 18 }}>
          <div className="ai-command-result-query">"{latest.query}"</div>

          <div className="ai-command-routing">
            <span className="soon-tag-inline">{t('aiCommand.routing')}</span>
            {latest.result.routing.map((step, i) => (
              <span key={i} className="ai-command-routing-step">
                {i > 0 && <span aria-hidden="true"> → </span>}
                {t(step.labelKey)}
              </span>
            ))}
          </div>

          {latest.result.kind === 'local_preview' ? (
            <>
              <div className="ai-command-result-badge">{t('aiCommand.localResult')}</div>
              <div className="ai-command-result-title">{latest.result.titleKey && t(latest.result.titleKey)}</div>
              {!latest.result.rows || latest.result.rows.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{latest.result.emptyKey && t(latest.result.emptyKey)}</div>
              ) : (
                <div>
                  {latest.result.rows.map((r, i) => (
                    <div key={i} className="overview-row" style={{ cursor: 'default' }}>
                      <span>{r.label}</span>
                      <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{r.value.startsWith('status.') || r.value.includes('.') ? t(r.value) : r.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {latest.result.route && (
                <button style={{ marginTop: 10 }} onClick={() => navigate(latest.result.route!)}>{t('aiCommand.viewAll')}</button>
              )}
            </>
          ) : latest.result.kind === 'ai_generated' ? (
            <>
              <div className="ai-command-result-badge">AI</div>
              <p style={{ fontSize: 13.5, color: 'var(--text-1)', whiteSpace: 'pre-wrap' }}>{latest.result.text}</p>
              {(latest.result.provider || latest.result.model) && (
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {[latest.result.provider, latest.result.model].filter(Boolean).join(' · ')}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="ai-command-result-badge warn">
                {latest.result.kind === 'ai_error' ? 'AI Error' : t('aiCommand.notConnectedTitle')}
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-2)' }}>
                {latest.result.kind === 'ai_error'
                  ? (latest.result.error ?? 'AI request failed')
                  : t('aiCommand.notConnectedBody')}
              </p>
            </>
          )}
        </div>
      )}

      <div className="overview-section-label" style={{ marginTop: 18 }}>{t('aiCommand.recent')}</div>
      <div className="card" style={{ padding: 12 }}>
        {history.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('aiCommand.noRecent')}</div>
        ) : (
          history.map((h, i) => (
            <div key={i} className="overview-row" onClick={() => submit(h.query)}>
              <span>{h.query}</span>
              <span className={`badge ${h.result.kind === 'local_preview' || h.result.kind === 'ai_generated' ? 'good' : 'neutral'}`}>
                {h.result.kind === 'local_preview'
                  ? t('aiCommand.localResult')
                  : h.result.kind === 'ai_generated'
                    ? 'AI'
                    : h.result.kind === 'ai_error'
                      ? 'AI Error'
                      : t('aiCommand.notConnectedTitle')}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
